import React, { useEffect, useState } from "react";
import {
  FaUsers,
  FaClock,
  FaArrowUp,
  FaFire,
  FaExclamationTriangle,
  FaChartLine,
  FaFilter,
  FaDownload,
  FaCalendarAlt,
  FaCheckCircle,
  FaSync,
} from "react-icons/fa";
import { useAppTheme } from "../../context/AppThemeContext";
import api from "../../api/httpClient";
import { API_BASES, API_PATHS } from "../../api/apiPaths";
import {
  getSuperAdminCountryVisitorAnalytics,
  getSuperAdminVisitorAnalytics,
  getSuperAdminVisitorLocationDiagnostics,
  normalizeVisitorAnalytics,
} from "../../api/visitorAnalytics";
import ClassNXLoader from "../ClassNXLoader";

export default function SuperAdminPlatformMetrics() {
  const { isDarkMode } = useAppTheme();
  const [metrics, setMetrics] = useState(null);
  const [objectStorage, setObjectStorage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("week"); // day, week, month
  const [selectedTab, setSelectedTab] = useState("overview"); // overview, users, features, activity
  const [visitorAnalyticsReload, setVisitorAnalyticsReload] = useState(0);
  const [refreshingVisitorAnalytics, setRefreshingVisitorAnalytics] = useState(false);
  const [countryAnalytics, setCountryAnalytics] = useState({ from: "", to: "", totalVisitors: 0, countries: [], records: [] });
  const [countryAnalyticsLoading, setCountryAnalyticsLoading] = useState(true);
  const [countryAnalyticsError, setCountryAnalyticsError] = useState(false);
  const [locationDiagnostics, setLocationDiagnostics] = useState(null);
  const [locationDiagnosticsLoading, setLocationDiagnosticsLoading] = useState(false);
  const [locationDiagnosticsError, setLocationDiagnosticsError] = useState("");
  const [visitorAnalytics, setVisitorAnalytics] = useState({
    todayVisitors: 0,
    onlineVisitors: 0,
    onlineLoggedInUsers: 0,
    dailyActiveUsers: 0,
    monthlyActiveUsers: 0,
    currentActiveUsers: 0,
    dailyVisitors: [],
    liveUsers: [],
    visitorRecords: [],
    checkedAt: "",
  });

  // Fetch platform metrics
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const response = await api.get(`/api/usage/platform-metrics?range=${encodeURIComponent(timeRange)}`);

        setMetrics(response?.data?.data ?? response?.data ?? null);
      } catch (error) {
        console.error("Error fetching metrics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, [timeRange]);

  useEffect(() => {
    let active = true;
    const fetchObjectStorage = async () => {
      try {
        const response = await api.get(API_PATHS.superAdmin.objectStorage());
        const payload = response?.data?.data ?? response?.data ?? {};
        if (active) {
          setObjectStorage({
            capacityGB: Number(payload.capacityBytes || 0) / (1024 ** 3),
            availableGB: Number(payload.availableBytes || 0) / (1024 ** 3),
            filesystemUsedGB: Number(payload.filesystemUsedBytes || 0) / (1024 ** 3),
            encryptedObjectGB: Number(payload.encryptedObjectBytes || 0) / (1024 ** 3),
            objectCount: Number(payload.objectCount || 0),
          });
        }
      } catch (error) {
        console.error("Error fetching object storage metrics:", error);
      }
    };
    fetchObjectStorage();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const days = timeRange === "month" ? 30 : timeRange === "week" ? 7 : 1;

    const loadVisitorAnalytics = async () => {
      try {
        const analytics = await getSuperAdminVisitorAnalytics(days);
        if (active) setVisitorAnalytics(analytics);
      } catch (error) {
        console.error("Error fetching visitor analytics:", error);
      } finally {
        if (active) setRefreshingVisitorAnalytics(false);
      }
    };

    // Load once on entry/range change. Live updates are delivered by the SSE
    // effect below, so polling here would duplicate an expensive analytics
    // request while the stream is connected.
    loadVisitorAnalytics();
    return () => {
      active = false;
    };
  }, [timeRange, visitorAnalyticsReload]);

  useEffect(() => {
    let active = true;
    const days = timeRange === "month" ? 30 : timeRange === "week" ? 7 : 1;

    const loadCountryAnalytics = async () => {
      setCountryAnalyticsLoading(true);
      setCountryAnalyticsError(false);
      try {
        const analytics = await getSuperAdminCountryVisitorAnalytics(days);
        if (active) setCountryAnalytics(analytics);
      } catch (error) {
        console.error("Error fetching country visitor analytics:", error);
        if (active) setCountryAnalyticsError(true);
      } finally {
        if (active) setCountryAnalyticsLoading(false);
      }
    };

    loadCountryAnalytics();
    return () => {
      active = false;
    };
  }, [timeRange, visitorAnalyticsReload]);

  useEffect(() => {
    const days = timeRange === "month" ? 30 : timeRange === "week" ? 7 : 1;
    const source = new EventSource(
      `${API_BASES.app}${API_PATHS.superAdmin.visitorAnalyticsStream(days)}`,
      { withCredentials: true }
    );

    const handlePresenceChanged = (event) => {
      try {
        setVisitorAnalytics(normalizeVisitorAnalytics(JSON.parse(event.data)));
      } catch {
        // Ignore malformed stream events; the fallback request will reconcile state.
      }
    };

    source.addEventListener("presence_changed", handlePresenceChanged);
    return () => {
      source.removeEventListener("presence_changed", handlePresenceChanged);
      source.close();
    };
  }, [timeRange]);

  const refreshVisitorAnalytics = () => {
    setRefreshingVisitorAnalytics(true);
    setVisitorAnalyticsReload((current) => current + 1);
  };

  const runLocationDiagnostics = async () => {
    if (locationDiagnosticsLoading) return;
    setLocationDiagnosticsLoading(true);
    setLocationDiagnosticsError("");
    try {
      setLocationDiagnostics(await getSuperAdminVisitorLocationDiagnostics());
    } catch (error) {
      setLocationDiagnostics(null);
      setLocationDiagnosticsError(error?.message || "Visitor location diagnostics could not be loaded.");
    } finally {
      setLocationDiagnosticsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen px-4 py-8 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
        <ClassNXLoader label="Loading platform metrics" detail="Collecting your latest platform activity." />
      </div>
    );
  }

  const defaultMetrics = {
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    unusedUsers: 0,
    totalPlatformTime: 0,
    averageSessionTime: 0,
    dailyActiveUsers: 0,
    weeklyActiveUsers: 0,
    monthlyActiveUsers: 0,
    topFeatures: [],
    usersByUsageLevel: [],
    dailyActivity: [],
    hourlyActivity: [],
  };

  const data = { ...defaultMetrics, ...(metrics || {}) };
  const totalPlatformSeconds = Number(data.totalPlatformTime) || 0;
  const platformHours = Math.floor(totalPlatformSeconds / 3600);
  const platformMinutes = Math.floor((totalPlatformSeconds % 3600) / 60);
  const formatVisitorTimestamp = (value) => {
    if (!value) return "—";
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
  };
  const visitorRecords = visitorAnalytics.visitorRecords.length > 0
    ? visitorAnalytics.visitorRecords
    : countryAnalytics.records.length > 0
      ? countryAnalytics.records
      : countryAnalytics.countries.filter((item) => item.visitor_date || item.first_seen || item.last_seen || item.expires_at);

  // Export metrics as CSV
  const handleExport = () => {
    const csv = [
      ["Platform Usage Metrics Report"],
      [`Generated: ${new Date().toLocaleString()}`],
      [""],
      ["Metric", "Value"],
      ["Total Users", data.totalUsers],
      ["Active Users", data.activeUsers],
      ["Inactive Users", data.inactiveUsers],
      ["Unused Users", data.unusedUsers],
      ["Current Active Users (5 minutes)", visitorAnalytics.currentActiveUsers],
      ["Daily Active Users", visitorAnalytics.dailyActiveUsers],
      ["Monthly Active Users (30 days)", visitorAnalytics.monthlyActiveUsers],
      ["Visitors Today", visitorAnalytics.todayVisitors],
      ["Visitors Online Now", visitorAnalytics.onlineVisitors],
      ["Total Platform Time", `${Math.floor(data.totalPlatformTime / 3600)}h`],
      ["Average Session Time", `${Math.floor(data.averageSessionTime / 60)}m`],
    ].map((row) => row.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
    link.download = `platform-metrics-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
  };

  const tabConfig = [
    { id: "overview", label: "Overview", icon: FaChartLine },
    { id: "users", label: "Users", icon: FaUsers },
    { id: "features", label: "Features", icon: FaArrowUp },
    { id: "activity", label: "Activity", icon: FaCalendarAlt },
  ];

  return (
    <div className={`min-h-screen px-4 py-8 sm:px-6 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className={`text-3xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
              Platform Usage Metrics
            </h1>
            <p className={`mt-2 ${isDarkMode ? "text-slate-400" : "text-slate-600"}`}>
              Real-time analytics and engagement metrics
            </p>
          </div>

          <div className="flex gap-3">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              className={`rounded-lg border px-4 py-2.5 transition focus:outline-none focus:ring-2 ${
                isDarkMode
                  ? "border-slate-700 bg-slate-900 text-white focus:ring-blue-500/20"
                  : "border-slate-300 bg-white text-slate-900 focus:ring-blue-500/20"
              }`}
            >
              <option value="day">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>

            <button
              onClick={handleExport}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-teal-600 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:opacity-90"
            >
              <FaDownload /> Export
            </button>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[
            {
              icon: FaDownload,
              label: "Available Storage",
              value: objectStorage ? `${objectStorage.availableGB.toFixed(2)} GB` : "Unavailable",
              total: "",
              color: "from-indigo-600 to-violet-400",
            },
            {
              icon: FaChartLine,
              label: "Stored Objects",
              value: objectStorage ? objectStorage.objectCount.toLocaleString() : "Unavailable",
              total: "",
              color: "from-teal-600 to-cyan-400",
            },
            {
              icon: FaUsers,
              label: "Active Users",
              value: data.activeUsers,
              total: data.totalUsers,
              color: "from-green-600 to-green-400",
            },
            {
              icon: FaExclamationTriangle,
              label: "Inactive Users",
              value: data.inactiveUsers,
              total: data.totalUsers,
              color: "from-yellow-600 to-yellow-400",
            },
            {
              icon: FaFire,
              label: "Unused Users",
              value: data.unusedUsers,
              total: data.totalUsers,
              color: "from-red-600 to-red-400",
            },
            {
              icon: FaClock,
              label: "Avg Session Time",
              value: `${Math.floor(data.averageSessionTime / 60)}m`,
              total: "",
              color: "from-blue-600 to-blue-400",
            },
            {
              icon: FaUsers,
              label: "Visitors Today",
              value: visitorAnalytics.todayVisitors,
              total: "",
              color: "from-violet-600 to-fuchsia-400",
            },
            {
              icon: FaFire,
              label: "Online Now",
              value: visitorAnalytics.onlineVisitors,
              total: "",
              detail: "Visitors active now",
              color: "from-cyan-600 to-sky-400",
            },
            {
              icon: FaCheckCircle,
              label: "Current Active Users",
              value: visitorAnalytics.currentActiveUsers,
              total: "",
              detail: "Signed in · active within 5 minutes",
              color: "from-emerald-600 to-teal-400",
            },
            {
              icon: FaCalendarAlt,
              label: "Daily Active Users",
              value: visitorAnalytics.dailyActiveUsers,
              total: "",
              detail: "Distinct signed-in users active today",
              color: "from-blue-600 to-indigo-400",
            },
            {
              icon: FaChartLine,
              label: "Monthly Active Users",
              value: visitorAnalytics.monthlyActiveUsers,
              total: "",
              detail: "Distinct signed-in users active in 30 days",
              color: "from-fuchsia-600 to-pink-400",
            },
          ].map((card, idx) => (
            <div
              key={idx}
              className={`overflow-hidden rounded-xl border ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}
            >
              <div className={`bg-gradient-to-r ${card.color} h-2`} />
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`text-xs font-semibold uppercase tracking-wider ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                      {card.label}
                    </p>
                    <p className={`mt-3 text-3xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                      {card.value}
                    </p>
                    {card.detail ? (
                      <p className={`mt-2 text-xs ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>{card.detail}</p>
                    ) : card.total && (
                      <p className={`mt-2 text-xs ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                        of {card.total} total ({Math.round((card.value / card.total) * 100)}%)
                      </p>
                    )}
                  </div>
                  <card.icon className={`text-3xl text-white/20`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className={`mb-8 overflow-hidden rounded-2xl border ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className={`border-b ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
            <div className="flex overflow-x-auto">
              {tabConfig.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`flex items-center gap-2 border-b-2 px-6 py-4 text-sm font-semibold transition ${
                    selectedTab === tab.id
                      ? isDarkMode
                        ? "border-blue-500 text-blue-400"
                        : "border-blue-600 text-blue-600"
                      : isDarkMode
                        ? "border-transparent text-slate-500 hover:text-slate-400"
                        : "border-transparent text-slate-600 hover:text-slate-900"
                  }`}
                >
                  <tab.icon className="text-lg" /> {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {/* Overview Tab */}
            {selectedTab === "overview" && (
              <div className="space-y-6">
                {/* Daily Activity */}
                <div>
                  <h3 className={`mb-4 text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                    Daily Activity
                  </h3>
                  <div className="space-y-3">
                    {data.dailyActivity?.slice(0, 7).map((day, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                            {day.date}
                          </span>
                          <span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                            {day.activeUsers} users
                          </span>
                        </div>
                        <div className={`h-2 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                          <div
                            className="h-full bg-gradient-to-r from-blue-600 to-teal-500 rounded-full"
                            style={{ width: `${Math.min(100, (day.activeUsers / (data.dailyActivity[0]?.activeUsers || 1)) * 100)}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className={`mb-4 text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>Visitor history</h3>
                  {visitorAnalytics.dailyVisitors.length > 0 ? <div className="space-y-3">
                    {visitorAnalytics.dailyVisitors.map((day, index) => (
                      <div key={day.date || index}>
                        <div className="mb-2 flex items-center justify-between"><span className={`text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{day.date || "Unknown date"}</span><span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>{Number(day.visitors ?? day.uniqueVisitors ?? 0).toLocaleString()} visitors</span></div>
                        <div className={`h-2 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}><div className="h-full rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500" style={{ width: `${Math.min(100, (Number(day.visitors ?? day.uniqueVisitors ?? 0) / Math.max(...visitorAnalytics.dailyVisitors.map((item) => Number(item.visitors ?? item.uniqueVisitors ?? 0)), 1)) * 100)}%` }} /></div>
                      </div>
                    ))}
                  </div> : <p className={`text-sm ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>Visitor history is not available yet.</p>}
                </div>

                <div>
                  <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
                    <div>
                      <h3 className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>Visitors by country</h3>
                      {countryAnalytics.from && countryAnalytics.to && (
                        <p className={`mt-1 text-xs ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                          {countryAnalytics.from} to {countryAnalytics.to}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {!countryAnalyticsLoading && !countryAnalyticsError && (
                        <span className={`text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                          {countryAnalytics.totalVisitors.toLocaleString()} total
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={runLocationDiagnostics}
                        disabled={locationDiagnosticsLoading}
                        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:cursor-wait disabled:opacity-60 ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-300 text-slate-700 hover:bg-slate-100"}`}
                      >
                        <FaFilter /> {locationDiagnosticsLoading ? "Running diagnostics..." : "Visitor Location Diagnostics"}
                      </button>
                    </div>
                  </div>
                  {(locationDiagnostics || locationDiagnosticsError) && (
                    <div className={`mb-4 rounded-lg border p-4 ${locationDiagnosticsError ? (isDarkMode ? "border-red-900/60 bg-red-950/20" : "border-red-200 bg-red-50") : (isDarkMode ? "border-blue-900/60 bg-blue-950/20" : "border-blue-200 bg-blue-50")}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className={`text-sm font-bold ${locationDiagnosticsError ? (isDarkMode ? "text-red-300" : "text-red-700") : (isDarkMode ? "text-blue-300" : "text-blue-800")}`}>Visitor Location Diagnostics</p>
                          {locationDiagnosticsError ? (
                            <p className={`mt-2 text-sm ${isDarkMode ? "text-red-300" : "text-red-700"}`}>{locationDiagnosticsError}</p>
                          ) : (
                            <dl className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                              {Object.entries(locationDiagnostics || {}).map(([key, value]) => (
                                <div key={key} className="min-w-0">
                                  <dt className={`text-[10px] font-bold uppercase tracking-wider ${isDarkMode ? "text-slate-500" : "text-slate-500"}`}>{key.replace(/([A-Z])/g, " $1").replace(/[_-]+/g, " ").trim()}</dt>
                                  <dd className={`mt-1 break-words text-sm font-semibold ${isDarkMode ? "text-slate-200" : "text-slate-800"}`}>{value === null || value === undefined || value === "" ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value)}</dd>
                                </div>
                              ))}
                            </dl>
                          )}
                        </div>
                        <button type="button" onClick={() => { setLocationDiagnostics(null); setLocationDiagnosticsError(""); }} aria-label="Close visitor location diagnostics" className={`shrink-0 text-lg ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>&times;</button>
                      </div>
                    </div>
                  )}
                  {countryAnalyticsLoading ? (
                    <p className={`text-sm ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>Loading country analytics...</p>
                  ) : countryAnalyticsError ? (
                    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4 ${isDarkMode ? "border-red-900/60 bg-red-950/20" : "border-red-200 bg-red-50"}`}>
                      <p className={`text-sm ${isDarkMode ? "text-red-300" : "text-red-700"}`}>Country analytics could not be loaded.</p>
                      <button type="button" onClick={refreshVisitorAnalytics} className={`text-sm font-semibold ${isDarkMode ? "text-blue-400" : "text-blue-700"}`}>Try again</button>
                    </div>
                  ) : countryAnalytics.countries.length > 0 ? (
                    <div className="space-y-3">
                      {countryAnalytics.countries.map((item, index) => (
                        <div key={`${item.country}-${index}`}>
                          <div className="mb-2 flex items-center justify-between gap-4">
                            <span className={`truncate text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>{item.country}</span>
                            <span className={`shrink-0 text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>{item.visitors.toLocaleString()} visitors</span>
                          </div>
                          <div className={`h-2 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                            <div className="h-full rounded-full bg-gradient-to-r from-emerald-600 to-teal-400" style={{ width: `${Math.min(100, (item.visitors / Math.max(countryAnalytics.countries[0]?.visitors || 0, 1)) * 100)}%` }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className={`text-sm ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>No visitors were recorded for this range.</p>
                  )}
                </div>

                <div>
                  <h3 className={`mb-4 text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>Visitor records</h3>
                  <div className={`overflow-x-auto rounded-lg border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                    <table className="w-full min-w-[850px] text-left text-sm">
                      <thead className={isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-600"}>
                        <tr><th className="px-4 py-3 font-semibold">Visitor date</th><th className="px-4 py-3 font-semibold">Country</th><th className="px-4 py-3 font-semibold">First seen</th><th className="px-4 py-3 font-semibold">Last seen</th><th className="px-4 py-3 font-semibold">Expires at</th></tr>
                      </thead>
                      <tbody>
                        {visitorRecords.length > 0 ? visitorRecords.map((record, index) => (
                          <tr key={record.id || record.visitorId || `${record.visitor_date || record.visitorDate}-${record.country}-${index}`} className={`border-t ${isDarkMode ? "border-slate-800 text-slate-200" : "border-slate-200 text-slate-700"}`}>
                            <td className="px-4 py-3">{record.visitor_date || record.visitorDate || "—"}</td>
                            <td className="px-4 py-3 font-medium">{record.country || "Unknown"}</td>
                            <td className="px-4 py-3">{formatVisitorTimestamp(record.first_seen || record.firstSeen)}</td>
                            <td className="px-4 py-3">{formatVisitorTimestamp(record.last_seen || record.lastSeen)}</td>
                            <td className="px-4 py-3">{formatVisitorTimestamp(record.expires_at || record.expiresAt)}</td>
                          </tr>
                        )) : <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No detailed visitor records are available for this range.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Total Platform Time */}
                <div className={`rounded-lg border p-6 ${isDarkMode ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}>
                  <p className={`text-sm font-semibold uppercase ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                    Total Platform Time
                  </p>
                  <p className={`mt-2 text-4xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                    {platformHours}h {platformMinutes}m
                  </p>
                </div>
              </div>
            )}

            {/* Users Tab */}
            {selectedTab === "users" && (
              <div className="space-y-6">
                {/* Usage Level Distribution */}
                <div>
                  <h3 className={`mb-4 text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                    Users by Usage Level
                  </h3>
                  <div className="space-y-3">
                    {[
                      { label: "Heavy Users", count: data.usersByUsageLevel?.heavy || 0, color: "bg-green-500" },
                      { label: "Regular Users", count: data.usersByUsageLevel?.regular || 0, color: "bg-blue-500" },
                      { label: "Light Users", count: data.usersByUsageLevel?.light || 0, color: "bg-yellow-500" },
                      { label: "Inactive", count: data.usersByUsageLevel?.inactive || 0, color: "bg-red-500" },
                    ].map((level, idx) => (
                      <div key={idx}>
                        <div className="flex items-center justify-between mb-2">
                          <span className={`text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                            {level.label}
                          </span>
                          <span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                            {level.count} ({Math.round((level.count / data.totalUsers) * 100)}%)
                          </span>
                        </div>
                        <div className={`h-3 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                          <div
                            className={`h-full ${level.color} rounded-full`}
                            style={{ width: `${(level.count / data.totalUsers) * 100}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* User Statistics */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Daily Active Users", value: data.dailyActiveUsers },
                    { label: "Weekly Active Users", value: data.weeklyActiveUsers },
                    { label: "Monthly Active Users", value: data.monthlyActiveUsers },
                    { label: "New Users Today", value: data.newUsersToday || 0 },
                  ].map((stat, idx) => (
                    <div
                      key={idx}
                      className={`rounded-lg border p-4 ${isDarkMode ? "border-slate-800 bg-slate-800/30" : "border-slate-200 bg-slate-50"}`}
                    >
                      <p className={`text-xs font-semibold uppercase ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                        {stat.label}
                      </p>
                      <p className={`mt-2 text-2xl font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                        {stat.value}
                      </p>
                    </div>
                  ))}
                </div>

                <div>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                    <h3 className={`text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>Logged-in users online</h3>
                    <div className="flex items-center gap-2"><span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>{visitorAnalytics.onlineLoggedInUsers} active now</span><button type="button" onClick={refreshVisitorAnalytics} disabled={refreshingVisitorAnalytics} title="Refresh online users" aria-label="Refresh online users" className={`inline-flex h-8 w-8 items-center justify-center border transition disabled:cursor-not-allowed disabled:opacity-60 ${isDarkMode ? "border-slate-700 text-slate-300 hover:bg-slate-800" : "border-slate-200 text-slate-600 hover:bg-slate-100"}`}><FaSync className={refreshingVisitorAnalytics ? "animate-spin" : ""} /></button></div>
                  </div>
                  <div className={`overflow-x-auto rounded-lg border ${isDarkMode ? "border-slate-800" : "border-slate-200"}`}>
                    <table className="w-full min-w-[700px] text-left text-sm">
                      <thead className={isDarkMode ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-600"}><tr><th className="px-4 py-3 font-semibold">Name</th><th className="px-4 py-3 font-semibold">Role</th><th className="px-4 py-3 font-semibold">Tenant</th><th className="px-4 py-3 font-semibold">Last active</th><th className="px-4 py-3 font-semibold">Status</th></tr></thead>
                      <tbody>{visitorAnalytics.liveUsers.length > 0 ? visitorAnalytics.liveUsers.map((user, index) => <tr key={user.id || user.userId || user.sessionId || index} className={`border-t ${isDarkMode ? "border-slate-800 text-slate-200" : "border-slate-200 text-slate-700"}`}><td className="px-4 py-3 font-medium">{user.displayName || user.name || "Unnamed user"}</td><td className="px-4 py-3">{user.role || "-"}</td><td className="px-4 py-3">{user.tenantName || user.tenant || "Platform"}</td><td className="px-4 py-3">{user.lastActiveAt ? new Date(user.lastActiveAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Now"}</td><td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-600"><FaCheckCircle className="text-emerald-500" />Active</span></td></tr>) : <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No logged-in users are online.</td></tr>}</tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Features Tab */}
            {selectedTab === "features" && (
              <div>
                <h3 className={`mb-4 text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                  Most Used Features
                </h3>
                <div className="space-y-3">
                  {data.topFeatures?.map((feature, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                          {idx + 1}. {feature.name}
                        </span>
                        <span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                          {feature.visits} visits
                        </span>
                      </div>
                      <div className={`h-2 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                        <div
                          className="h-full bg-gradient-to-r from-purple-600 to-pink-500"
                          style={{ width: `${(feature.visits / (data.topFeatures[0]?.visits || 1)) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Activity Tab */}
            {selectedTab === "activity" && (
              <div>
                <h3 className={`mb-4 text-lg font-bold ${isDarkMode ? "text-white" : "text-slate-900"}`}>
                  Hourly Activity
                </h3>
                <div className="space-y-3">
                  {data.hourlyActivity?.map((hour, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-2">
                        <span className={`text-sm font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                          {hour.hour}:00
                        </span>
                        <span className={`text-xs font-semibold ${isDarkMode ? "text-slate-500" : "text-slate-600"}`}>
                          {hour.users} users
                        </span>
                      </div>
                      <div className={`h-2 overflow-hidden rounded-full ${isDarkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                        <div
                          className="h-full bg-gradient-to-r from-cyan-600 to-blue-500"
                          style={{ width: `${(hour.users / (data.hourlyActivity[0]?.users || 1)) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
